import { useCallback } from 'react';

import { useBridgeApi } from '@/api/BridgeApi';
import { trackEvent } from '@/utils/fullstory';

import type { InspirationReference } from '../../../../bridge/types/artifact.ts';

import { asReasonList, renderLongReads } from './inspirationFormatting';

// Matches the cap the linked-item prompt block already applies to an artifact's
// content (services/py/chat/context_curator.py), so moving this analysis onto the
// system-message channel does not grow the prompt.
const ANALYSIS_CHAR_CAP = 4000;

export const buildInspirationContext = (
	artifact: InspirationReference,
): string => {
	const {
		topic,
		source_url,
		content_analysis,
		imagery_analysis,
		hook,
		pacing,
		payoff,
		suggestion,
		learning,
		learning_theme,
		format_name,
		why_it_works,
		found_by_job_for,
	} = artifact.content;
	// The Content space lists every active inspiration_reference, and
	// useContentArtifacts.ts filters on nothing else. So a post the daily job
	// found reaches this path too, and the customer never saved that one. Saying
	// they did makes the agent treat a rival business's post as the customer's
	// own reference.
	const foundFor = found_by_job_for?.trim();
	const provenance = foundFor
		? [
				`Bridgeway found this post while studying ${foundFor}.`,
				'The customer did not save it. It is reference material for new content. It is not editable, and it is not the subject of this conversation.',
			]
		: [
				'The customer started this conversation from a saved inspiration.',
				'It is reference material for new content. It is not editable, and it is not the subject of this conversation.',
			];
	const header = [
		...provenance,
		`Topic: ${topic || 'Untitled'}`,
		`Source: ${source_url}`,
		'',
	].join('\n');
	// The short reads get their room FIRST, and the long descriptive reads then
	// take what is left. A long content_analysis fills the whole budget on its
	// own, so appending these would push every one of them past the cut.
	const sections = (
		[
			// The lesson and its theme lead, because they are the transferable
			// reads the job paid for, and they are what later content recalls.
			['The lesson', learning],
			['Theme', learning_theme],
			['Repeatable format', format_name],
			['Why it works', asReasonList(why_it_works).join('; ')],
			['Hook', hook],
			['Pacing', pacing],
			['Payoff', payoff],
			['What to borrow', suggestion],
		] as const
	)
		.filter(([, body]) => body?.trim())
		.map(([label, body]) => `${label}: ${body}`)
		.join('\n');
	const sectionsBlock = sections ? `${sections}\n\n` : '';
	const room = ANALYSIS_CHAR_CAP - header.length - sectionsBlock.length;
	// Both long reads are labelled and share the room. The visual read is what
	// the vision call pays for, and this surface used to send neither it nor the
	// format name, so the agent could only discuss the caption.
	const longReads = renderLongReads(
		[
			['What it looks like', imagery_analysis],
			['What it says', content_analysis],
		],
		room,
	);
	return `${header}${sectionsBlock}${longReads}`.slice(0, ANALYSIS_CHAR_CAP);
};

// buildInspirationContext tells the truth about who saved the post, so the
// VISIBLE turn has to agree with it. initial_message is stored as the
// customer's own turn, so calling a post the daily job found "saved" puts a
// claim the customer never made into their own transcript, and hands the model
// two opposite provenance claims in one turn.
//
// Exported for the reason buildOpeningLine is exported in the job-space hook:
// this is the half the customer reads, so it earns its own test.
export const buildOpeningMessage = (artifact: InspirationReference): string => {
	const { topic, source_url, found_by_job_for } = artifact.content;
	const subject = found_by_job_for?.trim()
		? 'this post Bridgeway found'
		: 'this saved inspiration';
	// An uploaded design has no link, so naming one renders an empty pair of
	// brackets (ADO-1929).
	return source_url
		? `Use ${subject} to create new content: ${topic} (${source_url})`
		: `Use ${subject} to create new content: ${topic}`;
};

export const useInspirationChat = (artifact: InspirationReference) => {
	const bridgeApi = useBridgeApi();
	return useCallback(() => {
		trackEvent('Inspiration Used', { artifact_id: artifact.id });
		bridgeApi.sendAction({
			type: 'do_create_chat',
			initial_message: buildOpeningMessage(artifact),
			// The analysis reaches the model on the invisible system-message
			// channel. It used to travel as a linked_item, which also made the
			// article a clickable card, the agent's default edit target and the
			// thread's title — none of which the customer asked for (ADO-1907).
			system_message: buildInspirationContext(artifact),
			source_attribution: {
				kind: 'inspiration_reference',
				artifact_id: artifact.id,
			},
		});
	}, [bridgeApi, artifact]);
};
